import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Spin, Table } from 'antd';
import { DownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import getColumns from '../../UsersTable/getColumns';
import { selectDisplayColumnsForUsers } from '../../../redux/store/ui/common';
import { selectAuth } from '../../../redux/store/userData/user';
import { getFilteredColumns } from '../../../utils';
import { makeT } from '../messages';
import type { ParsedCsv } from './UploadStep';

type Props = {
  resolving: boolean;
  matched: any[];
  unmatched: string[];
  parsed: ParsedCsv | null;
  selection: { colIndex: number; values: string[] };
  selectedRecords: any[];
  setSelectedRecords: (rows: any[]) => void;
};

const PreviewStep: React.FC<Props> = ({
  resolving,
  matched,
  unmatched,
  parsed,
  selection,
  selectedRecords,
  setSelectedRecords,
}) => {
  const intl = useIntl();
  const t = makeT(intl);
  const user = useSelector(selectAuth);
  const displayColumns = useSelector(selectDisplayColumnsForUsers);
  const selectedBrand = user?.brands?.[0];

  const columns = useMemo(() => {
    const cols = getColumns({ intl, selectedBrand });
    return getFilteredColumns(cols, displayColumns);
  }, [intl, selectedBrand, displayColumns]);

  // size the antd Table's scroll area to the space the flex column gives us,
  // so the header + pagination stay pinned and only the table body scrolls.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(320);
  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const update = () => {
      // wrapper height minus antd header (~47) and pagination (~56)
      setScrollY(Math.max(160, el.clientHeight - 47 - 56));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [resolving, matched.length]);

  const totalIdentifiers = selection.values.length;
  const fileName = parsed?.fileName ?? '';

  // matched/unmatched CSV mirror the uploaded file (all original columns +
  // header), filtered by which identifier rows resolved to a user.
  const syntheticHeader = useMemo(
    () =>
      parsed?.header ??
      (parsed && parsed.rows[0]
        ? parsed.rows[0].map((_, i) => `column_${i + 1}`)
        : []),
    [parsed]
  );

  const buildCsv = (predicate: (idValue: string) => boolean) => {
    if (!parsed || selection.colIndex < 0) return [];
    const ci = selection.colIndex;
    const body = parsed.rows.filter((r) =>
      predicate(String(r[ci] ?? '').trim())
    );
    return [syntheticHeader, ...body];
  };

  const unmatchedSet = useMemo(
    () => new Set(unmatched.map((v) => String(v).trim())),
    [unmatched]
  );
  const unmatchedCsv = useMemo(
    () => buildCsv((v) => v !== '' && unmatchedSet.has(v)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parsed, selection.colIndex, unmatchedSet]
  );
  const matchedCsv = useMemo(
    () => buildCsv((v) => v !== '' && !unmatchedSet.has(v)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parsed, selection.colIndex, unmatchedSet]
  );

  if (resolving) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin />
        <div className="ba-help" style={{ marginTop: 12 }}>
          {t('fleetadmin.bulk.preview.resolving')}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="ba-preview-head">
        <div>
          <div className="ba-preview-title">
            {t('fleetadmin.bulk.preview.matched', {
              count: matched.length,
            })}
          </div>
          <div className="ba-preview-sub">
            {t('fleetadmin.bulk.preview.matchedSub', {
              total: totalIdentifiers,
              file: <span dir="ltr">{fileName}</span>,
            })}
          </div>
        </div>
        <CSVLink
          data={matchedCsv}
          filename="matched-users.csv"
          className="ba-csv-link"
        >
          <DownloadOutlined /> {t('fleetadmin.bulk.preview.downloadMatched')}
        </CSVLink>
      </div>

      {unmatched.length > 0 && (
        <div className="ba-notice">
          <div className="ba-notice-text">
            <ExclamationCircleOutlined
              style={{ color: '#F5A623', marginRight: 8 }}
            />
            {t('fleetadmin.bulk.preview.notFound', {
              count: unmatched.length,
            })}
          </div>
          <CSVLink
            data={unmatchedCsv}
            filename="unmatched-rows.csv"
            className="ba-csv-link"
          >
            <DownloadOutlined />{' '}
            {t('fleetadmin.bulk.preview.downloadUnmatched')}
          </CSVLink>
        </div>
      )}

      {matched.length > 0 && (
        <div className="ba-table-wrap" ref={wrapRef}>
          <Table
            rowKey={(r: any) => r.id}
            columns={columns}
            dataSource={matched}
            size="middle"
            scroll={{ y: scrollY, x: 'max-content' }}
            rowSelection={{
              selectedRowKeys: selectedRecords.map((r) => r.id),
              onChange: (_keys: any, rows: any[]) => setSelectedRecords(rows),
            }}
            pagination={{
              position: ['bottomLeft'],
              defaultPageSize: 50,
              pageSizeOptions: ['50', '100', '150', '200'],
              showSizeChanger: true,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default PreviewStep;
